import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DimDatatableColumn } from '@models';
import { ConsultaPaginadaViewModel } from '@models';
import { MessageService } from '../../../services/message.service';
import { ModalCadastroComponent } from '../../../shared/modal-cadastro/modal-cadastro.component';
import { ModalBasicComponent } from '../../../shared/modal-basic/modal-basic.component';
import { ContratosServiceV2 } from '@sv2/contratos/contratos-service-v2.service';
import { getErrorMessage, markFormGroupTouched, saveBlobToFile } from '@utils';
import { ContratoMensalidadeViewModel, DadosMensalidadeViewModel, FiltroContratosMensalidadesViewModel, GerarMensalidadesPayloadViewModel, LoadingMensalidadesState, MesViewModel, ModalGerarMensalidadesForm, PreviewMensalidadeViewModel } from './models';

@Component({
  selector: 'app-modal-gerar-mensalidades',
  templateUrl: './modal-gerar-mensalidades.component.html',
  styleUrls: ['./modal-gerar-mensalidades.component.css']
})
export class ModalGerarMensalidadesComponent extends ModalCadastroComponent implements OnInit {
  @ViewChild('modal', { static: true }) modal: ModalBasicComponent;

  constructor(private contratosServiceV2: ContratosServiceV2, private message: MessageService) {
    super();
  }

  meses: MesViewModel[] = [
    { id: 1, nome: 'Janeiro' },
    { id: 2, nome: 'Fevereiro' },
    { id: 3, nome: 'Março' },
    { id: 4, nome: 'Abril' },
    { id: 5, nome: 'Maio' },
    { id: 6, nome: 'Junho' },
    { id: 7, nome: 'Julho' },
    { id: 8, nome: 'Agosto' },
    { id: 9, nome: 'Setembro' },
    { id: 10, nome: 'Outubro' },
    { id: 11, nome: 'Novembro' },
    { id: 12, nome: 'Dezembro' }
  ];

  form = new FormGroup<ModalGerarMensalidadesForm>({
    numeroDe: new FormControl<number>(null),
    numeroAte: new FormControl<number>(null),
    clienteId: new FormControl<number>(null),
    diaFaturamento: new FormControl<number>(0),

    mesReferenciaCobranca: new FormControl<number>(null, [Validators.required]),
    mesReferenciaServico: new FormControl<number>(null, [Validators.required]),
    anoReferenciaServico: new FormControl<number>(new Date().getFullYear(), [Validators.required]),
    condicaoPagamentoId: new FormControl<number>(null, [Validators.required]),

    paramConsulta: new FormGroup({
      index: new FormControl(1),
      order: new FormGroup({
        dir: new FormControl('asc'),
        prop: new FormControl('numero'),
      })
    }),
  });

  loading: LoadingMensalidadesState = {
    request: false,
    table: false,
    gerar: false,
    preview: false,
  };

  columns: DimDatatableColumn[] = [
    { label: 'Número', col: 'numero', hide: false, columnStyle: { width: '1%' } },
    { label: 'Cliente', col: 'clienteNome', hide: false },
    { label: 'Valor Contrato', col: 'valorContrato', hide: false, currency: true },
    { label: 'Serviço Padrão*', col: 'servicoPadrao', hide: false, sortable: false, format: (v: boolean) => v ? 'Sim' : 'Não', columnStyle: { width: '1%' } },
    { label: 'Valor Total', col: 'valorTotal', hide: false, sortable: false, currency: true, columnStyle: { width: '1%' } },
    { label: 'Dia Cob.', col: 'diaCobranca', hide: false, sortable: false, columnStyle: { width: '1%' } },
    { label: 'Ação', col: 'preview', customTmpl: true },
  ];

  items: ContratoMensalidadeViewModel[] = [];
  selecionados: ContratoMensalidadeViewModel[] = [];
  searchData: ConsultaPaginadaViewModel<ContratoMensalidadeViewModel> = { items: [], pageIndex: 1, pageSize: 10, totalItems: 0, totalPages: 0 };
  previewItem: PreviewMensalidadeViewModel | null = null;

  ngOnInit(): void { }

  show() {
    this.modal.show();
  }

  fechar(value: any = false) {
    this.closed.emit(value);
  }

  onDiaFaturamentoChange(event: Event) {
    const target = event?.target as HTMLInputElement;
    const value = Number(target?.value ?? this.form.get('diaFaturamento')?.value ?? 0);
    const v = isNaN(value) ? 0 : value;
    this.form.get('diaFaturamento')?.setValue(v < 0 ? 0 : v > 31 ? 31 : v);
  }

  async pesquisar(): Promise<void> {
    try {
      this.loading.request = this.loading.table = true;

      const payload: FiltroContratosMensalidadesViewModel = {
        numeroDe: this.form.value.numeroDe,
        numeroAte: this.form.value.numeroAte,
        diaFaturamento: this.form.value.diaFaturamento,
        clienteId: this.form.value.clienteId,
        paramConsulta: {
          index: this.form.value.paramConsulta?.index ?? 1,
          order: {
            dir: this.form.value.paramConsulta?.order?.dir ?? 'asc',
            prop: this.form.value.paramConsulta?.order?.prop ?? 'numero',
          },
        },
      };

      const response = await this.contratosServiceV2.getPaginadaModalGerarMensalidades(payload);
      this.items = response.items || [];
      this.searchData = response || this.searchData;
    } catch (error) {
      this.message.error('Falha ao buscar contratos', 5000, 'Erro', true);
    } finally {
      this.loading.request = this.loading.table = false;
    }
  }

  setOrdenation(e: { order: { dir: string; prop: string } }): void {
    this.form.get('paramConsulta')?.get('order')?.setValue(e.order);
    this.pesquisar();
  }

  setPagination(e: { index: number }): void {
    this.form.get('paramConsulta')?.get('index')?.setValue(e.index);
    this.pesquisar();
  }

  get valorTotalGrade(): number {
    return (this.items || []).reduce((total: number, x: ContratoMensalidadeViewModel) => total + (Number(x.valorTotal) || 0), 0);
  }

  get valorTotalSelecionado(): number {
    return (this.selecionados || []).reduce((total: number, x: ContratoMensalidadeViewModel) => total + (Number(x.valorTotal) || 0), 0);
  }

  validarAntesGerar(): boolean {
    const dadosMensalidade = this.getDadosMensalidade();
    if (!dadosMensalidade.mesReferenciaCobranca || !dadosMensalidade.mesReferenciaServico || !dadosMensalidade.anoReferenciaServico || !dadosMensalidade.condicaoPagamentoId) {
      this.message.warning('Preencha os campos obrigatórios.', 6000, 'Aviso');
      return false;
    }

    if (!this.selecionados || this.selecionados.length === 0) {
      this.message.warning('Selecione ao menos um contrato.', 6000, 'Aviso');
      return false;
    }

    const dia = this.form.get('diaFaturamento')?.value;
    if (dia !== null && dia !== undefined) {
      if (Number(dia) < 0 || Number(dia) > 31) {
        this.message.warning('Dia de faturamento deve estar entre 0 e 31.', 6000, 'Aviso');
        return false;
      }
    }

    return true;
  }

  getDadosMensalidade(): DadosMensalidadeViewModel {
    return {
      mesReferenciaCobranca: this.form.get('mesReferenciaCobranca')?.value || 0,
      mesReferenciaServico: this.form.get('mesReferenciaServico')?.value || 0,
      anoReferenciaServico: this.form.get('anoReferenciaServico')?.value || new Date().getFullYear(),
      condicaoPagamentoId: this.form.get('condicaoPagamentoId')?.value || 0,
    };
  }

  handleOnGerar(): void {
    markFormGroupTouched(this.form);
    const validacao = this.validarAntesGerar();
    if (this.form.invalid || !validacao) {
      return;
    }

    const payload: GerarMensalidadesPayloadViewModel = {
      contratos: this.selecionados.map(c => ({
        contratoId: c.id,
        ...this.getDadosMensalidade()
      })),
    };

    this.closed.emit(payload);
  }

  async handleOnPreview(contrato: ContratoMensalidadeViewModel): Promise<void> {
    try {
      this.loading.preview = true;

      const response = await this.contratosServiceV2.downloadBlobAsPromise<number>(null, `gerarPreview/${contrato.id}`);
      const fileName = `ordem-servico-${contrato.numero}`
      if (await saveBlobToFile(response, fileName)) {
        this.message.success(`Exportação concluída. Procure pelo arquivo '${fileName}' na sua pasta de downloads.`);
      } else {
        this.message.success('Falha ao exportar.');
      }

    } catch (error) {
      this.message.error(`Falha ao previsualizar mensalidade: ${getErrorMessage(error)}`, 5000, 'Erro', true);
      console.error(error);
    } finally {
      this.loading.preview = false;
    }
  }
}
